import React from 'react'
import { useI18n } from '../../i18n'
import { componentScope } from '../../lib/cssScope'
import { ProblemCard } from '@/components/ui/ProblemCard'
import pageStyles from '@/styles/pages/Assets.module.css'

interface AssetsOverviewProps {
  stats: {
    totalAssets: number
    unknownAssets: number
    assetsWithVulnerabilities: number
    avgRiskScore: number
    newAssetsLast7Days: number
    complianceAvg: number
    edrCoverage: number
  }
}

/**
 * Top KPI row for /assets — neutral ProblemCard tiles, horizontal scroll on small screens.
 */
const AssetsOverview: React.FC<AssetsOverviewProps> = ({ stats }) => {
  const { t } = useI18n()

  const cards = [
    {
      id: 'totalAssets',
      title: t('assets.overview.totalAssets', 'Total Assets'),
      value: stats.totalAssets.toString(),
      footer: t('assets.overview.totalAssetsHint', 'All registered assets'),
    },
    {
      id: 'unknownAssets',
      title: t('assets.overview.unknownAssets', 'Unknown Assets'),
      value: stats.unknownAssets.toString(),
      footer: t('assets.overview.unknownAssetsHint', 'Assets without classification'),
    },
    {
      id: 'assetsWithVulnerabilities',
      title: t('assets.overview.assetsWithVulnerabilities', 'Assets with Vulnerabilities'),
      value: stats.assetsWithVulnerabilities.toString(),
      footer: t('assets.overview.assetsWithVulnerabilitiesHint', 'Assets requiring attention'),
    },
    {
      id: 'avgRiskScore',
      title: t('assets.overview.avgRiskScore', 'Avg Risk Score'),
      value: `${stats.avgRiskScore.toFixed(1)}%`,
      footer: t('assets.overview.avgRiskScoreHint', 'Average risk across all assets'),
    },
  ]

  return (
    <div
      className={`${componentScope('AssetsOverview')} ${pageStyles.horizontalRow}`}
      role="region"
      aria-label={t('assets.overview.regionLabel', 'Asset summary')}
    >
      {cards.map((card) => (
        <ProblemCard
          key={card.id}
          tone="neutral"
          title={card.title}
          value={card.value}
          footer={card.footer}
          className={pageStyles.problemCardSlot}
        />
      ))}
    </div>
  )
}

export default AssetsOverview
